import React, { useState, useContext, useEffect } from "react";
import { login_api } from "../../services/Api";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import rolling from "./rolling.svg";
import AppContext from "../../AppContext";
import { SUCCESS_RESULT, AZURE_SSO_REDIRECT_URL } from "../../Constants";
import {
  getCurrentReturnPath,
  saveRedirectUrlIfValid,
  getSafeRedirectUrl,
  applyAuthBootstrapData,
  getEncryptedUserData,
} from "../../services/Utils";

const buildAzureSsoUrl = () => {
  const origin = `${window.location.origin}/auth/callback`;
  return `${AZURE_SSO_REDIRECT_URL}?origin=${encodeURIComponent(origin)}`;
};

const isLocalhost = () => {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
};

const Login = () => {
  const history = useHistory();
  const global = useContext(AppContext);
  const [error, setError] = useState("");
  const [isLogging, setIsLogging] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ssoError, setSsoError] = useState(null);
  const showLocalLogin = isLocalhost();

  useEffect(() => {
    if (!showLocalLogin) return;
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") login();
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [showLocalLogin, email, password, isLogging]);

  const normalizeUserPhoto = (userData) => {
    const finalUserData = { ...userData };
    if (!finalUserData.photo) {
      finalUserData.photo =
        userData.image ||
        userData.avatar ||
        userData.picture ||
        userData.profile_picture ||
        userData.photo_url ||
        null;
    }
    return finalUserData;
  };

  const completeAuthenticatedLogin = (authData, userData) => {
    const saved = applyAuthBootstrapData(
      {
        ...authData,
        user: normalizeUserPhoto(userData || authData.user),
      },
      { token: authData.token }
    );
    if (!saved) {
      cogoToast.error("Failed to save session. Please try again.", { hideAfter: 5 });
      return false;
    }

    const verifyUser = getEncryptedUserData();
    if (!verifyUser) {
      cogoToast.error("Failed to verify user data. Please try again.", { hideAfter: 5 });
      return false;
    }

    global.setIs_loggedIn(true);
    const redirectUrl = localStorage.getItem("redirectUrl");
    const safeRedirectUrl = getSafeRedirectUrl(redirectUrl);

    setTimeout(() => {
      window.location.href = safeRedirectUrl;
      localStorage.removeItem("redirectUrl");
    }, 100);
    return true;
  };

  const login = () => {
    if (!showLocalLogin || isLogging) return;

    const user = {
      username: email.trim(),
      password: password.trim(),
      login_type: "local",
    };

    if (!user.username) return setError("Enter email address");
    if (!user.password) return setError("Enter your password");

    setError("");
    setIsLogging(true);

    login_api(user)
      .then((res) => {
        setIsLogging(false);
        const response = res.data;

        if (response.status === SUCCESS_RESULT) {
          const authData = response.data || {};
          if (!completeAuthenticatedLogin(authData, authData.user)) {
            return;
          }
        } else {
          cogoToast.error(response.message, { hideAfter: 5 });
        }
      })
      .catch(() => {
        setIsLogging(false);
        cogoToast.error("Something went wrong. Try again.", { hideAfter: 5 });
      });
  };

  const microsoftLogin = () => {
    const currentPath = getCurrentReturnPath();
    if (currentPath !== "/login" && currentPath !== "/auth/callback") {
      saveRedirectUrlIfValid(currentPath);
    }

    window.location.assign(buildAzureSsoUrl());
  };

  return (
    <div style={styles.page}>
      {/* LEFT PANEL */}
      <div className="leftPanel" style={styles.leftPanel}>
       
        <div style={styles.overlay}>
          <center>
            <img src="../images/logo.jpg" alt="Logo" style={styles.leftLogo} />
            <h1 style={styles.heading}>Welcome to EBMS</h1>
            <p style={styles.subText}>
              <span style={{ fontWeight: "bold", color: "#7CB342" }}>TRANSPARENCY</span>
              <span style={{ color: "#FFFFFF", margin: "0 8px" }}>|</span>
              <span style={{ fontWeight: "bold", color: "#FFFFFF" }}>EFFICIENCY</span>
              <span style={{ color: "#FFFFFF", margin: "0 8px" }}>|</span>
              <span style={{ fontWeight: "bold", color: "#FFFF00" }}>ACCOUNTABILITY</span>
            </p>
          </center>
        </div>

      </div>

      {/* RIGHT PANEL */}
      <div className="rightPanel" style={styles.rightPanel}>
        {ssoError ? (
          <div style={styles.card}>
            <div style={{
              textAlign: 'center',
              padding: '20px 0'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '20px'
              }}>
                🔄
              </div>
              <h2 style={{
                ...styles.title,
                color: '#0056a6',
                marginBottom: '10px'
              }}>
                One More Step Needed
              </h2>
              <p style={{
                fontSize: '16px',
                color: '#666',
                marginBottom: '25px',
                lineHeight: '1.6'
              }}>
                We're almost there! .
              </p>
              <div style={{
                backgroundColor: '#e7f3ff',
                border: '1px solid #b3d9ff',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '25px',
                color: '#004085',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'left'
              }}>
                <strong>What happened?</strong>
                <br />
                <span style={{ fontSize: '13px' }}>{ssoError}</span>
              </div>
              <button
                onClick={microsoftLogin}
                style={{
                  ...styles.primaryButton,
                  backgroundColor: '#0056a6',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <img
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgdmlld0JveD0iMCAwIDIxIDIxIj48dGl0bGU+TVMtU3ltYm9sTG9ja3VwPC90aXRsZT48cmVjdCB4PSIxIiB5PSIxIiB3aWR0aD0iOSIgaGVpZ2h0PSI5IiBmaWxsPSIjZjI1MDIyIi8+PHJlY3QgeD0iMSIgeT0iMTEiIHdpZHRoPSI5IiBoZWlnaHQ9IjkiIGZpbGw9IiMwMGE0ZWYiLz48cmVjdCB4PSIxMSIgeT0iMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iIzdmYmEwMCIvPjxyZWN0IHg9IjExIiB5PSIxMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iI2ZmYjkwMCIvPjwvc3ZnPg=="
                  alt="Microsoft"
                  style={styles.microsoftLogo}
                />
                Continue with Microsoft
              </button>
              <button
                onClick={() => {
                  setSsoError(null);
                  setEmail("");
                  document.getElementById("login")?.focus();
                }}
                style={{
                  ...styles.microsoftButton,
                  marginBottom: '15px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6'
                }}
              >
                Use Password Login Instead
              </button>
            </div>
          </div>
        ) : (
        <div style={styles.card}>
          <h2 style={styles.title}>Sign In</h2>

          {error && <div style={styles.error}>{error}</div>}

          {showLocalLogin && (
            <>
              <input
                id="login"
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                autoComplete="username"
              />
              <input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                autoComplete="current-password"
              />
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  marginBottom: 15,
                  opacity: isLogging ? 0.7 : 1,
                }}
                onClick={login}
                disabled={isLogging}
              >
                {isLogging ? (
                  <img src={rolling} alt="Loading" style={{ height: 20 }} />
                ) : (
                  "Sign In"
                )}
              </button>
              <div style={styles.divider}>
                <span style={styles.dividerText}>or</span>
              </div>
            </>
          )}

          <button style={styles.microsoftButton} onClick={microsoftLogin}>
            <img
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgdmlld0JveD0iMCAwIDIxIDIxIj48dGl0bGU+TVMtU3ltYm9sTG9ja3VwPC90aXRsZT48cmVjdCB4PSIxIiB5PSIxIiB3aWR0aD0iOSIgaGVpZ2h0PSI5IiBmaWxsPSIjZjI1MDIyIi8+PHJlY3QgeD0iMSIgeT0iMTEiIHdpZHRoPSI5IiBoZWlnaHQ9IjkiIGZpbGw9IiMwMGE0ZWYiLz48cmVjdCB4PSIxMSIgeT0iMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iIzdmYmEwMCIvPjxyZWN0IHg9IjExIiB5PSIxMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iI2ZmYjkwMCIvPjwvc3ZnPg=="
              alt="Microsoft"
              style={styles.microsoftLogo}
            />
            Sign in with Microsoft Account.
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

// STYLES
const styles = {
  page: {
    display: "flex",
    height: "calc(100vh / 0.80)",
    minHeight: "calc(100vh / 0.80)",
    width: "100%",
    backgroundColor: "#f5f7fb",
    fontFamily: "Inter, Arial, sans-serif",
  },
  leftPanel: {
    width: "50%",
    position: "relative",
    backgroundColor: "#004d99",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: -15,
    height: "100%",
    minHeight: "calc(100vh / 0.80)",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.25,
  },
  overlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    color: "#fff",
    width: "80%",
  },
  leftLogo: {
    width: 100,
    marginBottom: 25,
  },
  heading: {
    fontSize: 36,
    fontWeight: 700,
    marginBottom: 10,
  },
  subText: {
    fontSize: 20,
  },
  rightPanel: {
    width: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(to bottom right, #eaf3fc, #f9fbff)",
    height: "100%",
    minHeight: "calc(100vh / 0.80)",
  },
  card: {
    width: "90%",
    maxWidth: 420,
    background: "#fff",
    padding: "40px 35px",
    borderRadius: 16,
    boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  title: {
    color: "#222",
    marginBottom: 25,
    fontSize: 24,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    marginBottom: 15,
    border: "1px solid #ddd",
    borderRadius: 8,
    outline: "none",
    fontSize: 15,
  },
  primaryButton: {
    width: "100%",
    backgroundColor: "#0056a6",
    color: "#fff",
    padding: "12px 0",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 500,
    cursor: "pointer",
  },
  microsoftButton: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #ccc",
    padding: "12px 0",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
    color: "#000",
  },
  microsoftLogo: {
    width: 20,
    height: 20,
  },
  divider: {
    position: "relative",
    textAlign: "center",
    margin: "20px 0",
  },
  dividerText: {
    background: "#fff",
    padding: "0 10px",
    color: "#999",
    fontSize: 14,
  },
  spinner: {
    margin: "15px 0",
  },
  error: {
    color: "red",
    fontSize: 13,
    marginBottom: 10,
  },
  footer: {
    marginTop: 20,
    color: "#555",
    fontSize: 13,
  },
  link: {
    color: "#0056a6",
    cursor: "pointer",
    textDecoration: "underline",
  },
  version: {
    display: "block",
    marginTop: 10,
    color: "#aaa",
  },
};

// Responsive adjustment
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 768px) {
    .leftPanel {
      display: none !important;
    }
    .rightPanel {
      width: 100% !important;
      background: #f5f7fb !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default Login;
